import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "../lib/api";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Switch } from "./ui/switch";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Area, ComposedChart,
} from "recharts";
import { Loader2, Play, Plus, Trash2, Wallet, TrendingUp, Coins } from "lucide-react";
import { formatUSD, formatPct } from "../lib/format";
import { toast } from "sonner";

const ASSET_PRESETS = [
  { symbol: "BTC-USD", label: "Bitcoin" },
  { symbol: "ETH-USD", label: "Ethereum" },
  { symbol: "SOL-USD", label: "Solana" },
  { symbol: "SPY", label: "S&P 500" },
  { symbol: "QQQ", label: "Nasdaq 100" },
  { symbol: "AAPL", label: "Apple" },
  { symbol: "NVDA", label: "NVIDIA" },
  { symbol: "TSLA", label: "Tesla" },
  { symbol: "COIN", label: "Coinbase" },
  { symbol: "MSTR", label: "Strategy" },
  { symbol: "GC=F", label: "Or" },
  { symbol: "SI=F", label: "Argent" },
  { symbol: "URA", label: "Uranium" },
  { symbol: "TLT", label: "US 20Y+ Treasury" },
];

function Stat({ label, value, positive }) {
  return (
    <div className="p-4 rounded-lg border border-white/5 bg-white/[0.02]">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className={`text-xl font-mono-num font-semibold mt-1 ${positive === true ? "text-emerald-400" : positive === false ? "text-rose-400" : ""}`}>
        {value}
      </div>
    </div>
  );
}

export default function WalletSimulator() {
  const [assets, setAssets] = useState([
    { symbol: "BTC-USD", weight_pct: 50 },
    { symbol: "SPY", weight_pct: 30 },
    { symbol: "GC=F", weight_pct: 20 },
  ]);
  const [dcaAmount, setDcaAmount] = useState(500);
  const [frequency, setFrequency] = useState("monthly");
  const [strategy, setStrategy] = useState("fixed");
  const [startDate, setStartDate] = useState("2021-01-01");
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));
  const [tpEnabled, setTpEnabled] = useState(false);
  const [tpTrigger, setTpTrigger] = useState(50);
  const [tpSell, setTpSell] = useState(25);
  const [result, setResult] = useState(null);

  const totalWeight = assets.reduce((s, a) => s + (parseFloat(a.weight_pct) || 0), 0);
  const weightsOk = Math.abs(totalWeight - 100) < 0.01;
  const isCompare = strategy === "compare";
  const isDynamic = strategy === "dynamic";

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        assets: assets.map((a) => ({ symbol: a.symbol.toUpperCase(), weight_pct: parseFloat(a.weight_pct) || 0 })),
        dca_amount: parseFloat(dcaAmount) || 0,
        frequency,
        strategy,
        start_date: startDate,
        end_date: endDate,
        take_profit_trigger_pct: tpEnabled ? parseFloat(tpTrigger) : null,
        take_profit_sell_pct: tpEnabled ? parseFloat(tpSell) : null,
      };
      return (await api.post("/backtest/wallet", payload)).data;
    },
    onSuccess: (d) => {
      setResult(d);
      toast.success("Simulation wallet exécutée");
    },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message),
  });

  const updateAsset = (i, field, val) => {
    const next = [...assets];
    next[i] = { ...next[i], [field]: val };
    setAssets(next);
  };

  const removeAsset = (i) => setAssets(assets.filter((_, idx) => idx !== i));
  const addAsset = () => setAssets([...assets, { symbol: "ETH-USD", weight_pct: 10 }]);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <Card className="p-6 bg-card border-white/5 lg:col-span-1 h-fit" data-testid="wallet-form">
        <div className="flex items-center gap-2 mb-4">
          <Wallet className="w-5 h-5 text-emerald-400" />
          <h3 className="font-display font-bold text-lg">Wallet DCA</h3>
        </div>

        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Actifs (poids %)</Label>
              <span className={`text-xs font-mono-num ${weightsOk ? "text-emerald-400" : "text-amber-400"}`} data-testid="wallet-total-weight">
                Total : {totalWeight.toFixed(1)}%
              </span>
            </div>
            <div className="space-y-2">
              {assets.map((a, i) => (
                <div key={i} className="flex gap-2" data-testid={`wallet-asset-row-${i}`}>
                  <Select value={a.symbol} onValueChange={(v) => updateAsset(i, "symbol", v)}>
                    <SelectTrigger className="bg-white/[0.03] border-white/10 flex-1" data-testid={`wallet-asset-select-${i}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ASSET_PRESETS.map((p) => (
                        <SelectItem key={p.symbol} value={p.symbol}>
                          {p.label} <span className="text-zinc-500 ml-2">{p.symbol}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    className="w-20 bg-white/[0.03] border-white/10 font-mono-num"
                    value={a.weight_pct}
                    onChange={(e) => updateAsset(i, "weight_pct", e.target.value)}
                    data-testid={`wallet-asset-weight-${i}`}
                  />
                  <button
                    onClick={() => removeAsset(i)}
                    className="p-2 rounded hover:bg-rose-500/10 text-rose-400 shrink-0"
                    data-testid={`wallet-asset-remove-${i}`}
                    aria-label="Retirer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            <Button onClick={addAsset} variant="outline" size="sm" className="mt-2 w-full border-white/10 bg-transparent hover:bg-white/5" data-testid="wallet-add-asset">
              <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter un actif
            </Button>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Montant total DCA ($)</Label>
            <Input type="number" value={dcaAmount} onChange={(e) => setDcaAmount(e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10 font-mono-num" data-testid="wallet-dca-amount" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Stratégie</Label>
            <Select value={strategy} onValueChange={setStrategy}>
              <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="wallet-strategy">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="fixed">DCA classique (montant fixe)</SelectItem>
                <SelectItem value="dynamic">DCA dynamique (RSI + MM200)</SelectItem>
                <SelectItem value="compare">Comparer classique vs dynamique</SelectItem>
              </SelectContent>
            </Select>
            {isDynamic && (
              <p className="text-[10px] text-zinc-500 mt-1.5 leading-relaxed">
                Achat plus lourd si RSI &lt; 30 ou prix &lt; MM200, plus léger si RSI &gt; 70 ou prix &gt; +20%. Prises de profit progressives selon RSI et gain.
              </p>
            )}
            {isCompare && (
              <p className="text-[10px] text-zinc-500 mt-1.5">
                Compare les deux stratégies sur la même période avec les mêmes actifs.
              </p>
            )}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Fréquence</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="wallet-frequency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Journalière</SelectItem>
                <SelectItem value="weekly">Hebdomadaire</SelectItem>
                <SelectItem value="monthly">Mensuelle</SelectItem>
                <SelectItem value="annual">Annuelle</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Début</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="wallet-start" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400">Fin</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10" data-testid="wallet-end" />
            </div>
          </div>

          <div className="p-3 rounded-lg border border-white/5 bg-white/[0.02] space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-[0.15em] text-emerald-400">Take-profit</Label>
              <Switch checked={tpEnabled} onCheckedChange={setTpEnabled} data-testid="wallet-tp-toggle" />
            </div>
            {tpEnabled && (
              <>
                <div>
                  <Label className="text-xs text-zinc-400">Si un actif gagne (+%)</Label>
                  <Input type="number" value={tpTrigger} onChange={(e) => setTpTrigger(e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10 font-mono-num" data-testid="wallet-tp-trigger" />
                </div>
                <div>
                  <Label className="text-xs text-zinc-400">Vendre (% de la position)</Label>
                  <Input type="number" value={tpSell} onChange={(e) => setTpSell(e.target.value)} className="mt-1.5 bg-white/[0.03] border-white/10 font-mono-num" data-testid="wallet-tp-sell" />
                </div>
                <p className="text-[10px] text-zinc-500 leading-relaxed">
                  Exemple : trigger 50 / sell 25 → dès qu'une position est à +50%, on encaisse 25% des parts.
                </p>
              </>
            )}
          </div>

          {!weightsOk && (
            <div className="p-3 rounded-lg border border-amber-500/30 bg-amber-500/10 text-[11px] text-amber-300" data-testid="wallet-weight-error">
              Le total de vos allocations est de <span className="font-mono-num">{totalWeight.toFixed(1)}%</span>. Ajustez-les pour arriver à 100 % avant de lancer la simulation.
            </div>
          )}

          <Button onClick={() => mutation.mutate()} disabled={mutation.isPending || assets.length === 0 || !weightsOk} className="w-full bg-emerald-500 text-black hover:bg-emerald-300 mt-2" data-testid="wallet-run-btn">
            {mutation.isPending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Simulation…</>
            ) : (
              <><Play className="w-4 h-4 mr-2" /> Simuler le wallet</>
            )}
          </Button>
        </div>
      </Card>

      <div className="lg:col-span-2 space-y-6">
        {result ? (
          result.mode === "compare" ? (
            <>
              <div className="grid grid-cols-2 gap-4" data-testid="wallet-compare-metrics">
                {[
                  { key: "fixed", label: "DCA classique", color: "#a1a1aa" },
                  { key: "dynamic", label: "DCA dynamique", color: "#00BF63" },
                ].map((col) => {
                  const r = result[col.key];
                  return (
                    <Card key={col.key} className="p-5 bg-card border-white/5" data-testid={`wallet-compare-${col.key}`}>
                      <div className="flex items-center gap-2 mb-4">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ background: col.color }} />
                        <h4 className="font-display font-bold">{col.label}</h4>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <Stat label="Investi" value={formatUSD(r.total_invested)} />
                        <Stat label="Valeur finale" value={formatUSD(r.final_value)} positive={r.final_value >= r.total_invested} />
                        <Stat label="Rendement" value={formatPct(r.total_return_pct)} positive={r.total_return_pct >= 0} />
                        <Stat label="Max Drawdown" value={formatPct(r.max_drawdown_pct)} positive={false} />
                        <Stat label="CAGR" value={formatPct(r.cagr_pct)} positive={r.cagr_pct >= 0} />
                        <Stat label="# Ordres" value={r.num_trades} />
                      </div>
                    </Card>
                  );
                })}
              </div>

              <Card className="p-6 bg-card border-white/5" data-testid="wallet-compare-chart">
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <h3 className="font-display font-bold text-lg">Classique vs Dynamique</h3>
                </div>
                <div className="h-[380px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={result.combined_curve}>
                      <CartesianGrid stroke="#27272A" strokeDasharray="3 3" opacity={0.4} />
                      <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} />
                      <YAxis stroke="#71717A" fontSize={11} tickLine={false} tickFormatter={(v) => formatUSD(v, 0)} />
                      <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 8 }} formatter={(v) => formatUSD(v)} />
                      <Line type="monotone" dataKey="invested" stroke="#71717A" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="Investi" />
                      <Line type="monotone" dataKey="fixed" stroke="#a78bfa" strokeWidth={2} dot={false} name="Classique" />
                      <Line type="monotone" dataKey="dynamic" stroke="#00BF63" strokeWidth={2.5} dot={false} name="Dynamique" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-3 text-xs text-zinc-500">
                  Écart de valeur finale : <span className="font-mono-num text-zinc-200">{formatUSD(result.dynamic.final_value - result.fixed.final_value)}</span>
                  {" · "}Écart de drawdown : <span className="font-mono-num text-zinc-200">{formatPct(result.dynamic.max_drawdown_pct - result.fixed.max_drawdown_pct)}</span>
                </div>
              </Card>
            </>
          ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="wallet-metrics">
              <Stat label="Total investi" value={formatUSD(result.total_invested)} />
              <Stat label="Valeur finale" value={formatUSD(result.final_value)} positive={result.final_value >= result.total_invested} />
              <Stat label="Rendement" value={formatPct(result.total_return_pct)} positive={result.total_return_pct >= 0} />
              <Stat label="CAGR" value={formatPct(result.cagr_pct)} positive={result.cagr_pct >= 0} />
              <Stat label="Max Drawdown" value={formatPct(result.max_drawdown_pct)} positive={false} />
              <Stat label="Cash réalisé (TP)" value={formatUSD(result.realized_cash)} />
              <Stat label="# Ordres" value={result.num_trades} />
              <Stat label="# Actifs" value={result.breakdown.length} />
            </div>

            <Card className="p-6 bg-card border-white/5" data-testid="wallet-chart">
              <div className="flex items-center gap-2 mb-4">
                <TrendingUp className="w-4 h-4 text-emerald-400" />
                <h3 className="font-display font-bold text-lg">Valeur du portefeuille vs. investi</h3>
              </div>
              <div className="h-[360px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={result.equity_curve}>
                    <defs>
                      <linearGradient id="cInvested" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#F59E0B" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#F59E0B" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke="#27272A" strokeDasharray="3 3" opacity={0.4} />
                    <XAxis dataKey="date" stroke="#71717A" fontSize={11} tickLine={false} />
                    <YAxis stroke="#71717A" fontSize={11} tickLine={false} tickFormatter={(v) => formatUSD(v, 0)} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #27272A", borderRadius: 8 }} labelStyle={{ color: "#A1A1AA" }} formatter={(v) => formatUSD(v)} />
                    <Area type="monotone" dataKey="invested" stroke="#F59E0B" fill="url(#cInvested)" strokeWidth={1.5} strokeDasharray="4 4" />
                    <Line type="monotone" dataKey="value" stroke="#00BF63" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card className="p-6 bg-card border-white/5" data-testid="wallet-breakdown">
              <div className="flex items-center gap-2 mb-4">
                <Coins className="w-4 h-4 text-emerald-400" />
                <h3 className="font-display font-bold text-lg">Détail par actif</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-[0.15em] text-zinc-500 border-b border-white/5">
                      <th className="py-2">Actif</th>
                      <th className="py-2 text-right">Parts</th>
                      <th className="py-2 text-right">Investi</th>
                      <th className="py-2 text-right">Valeur</th>
                      <th className="py-2 text-right">Perf</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.breakdown.map((b) => (
                      <tr key={b.symbol} className="border-b border-white/5" data-testid={`wallet-breakdown-${b.symbol}`}>
                        <td className="py-2.5 font-mono-num text-emerald-400">{b.symbol}</td>
                        <td className="py-2.5 text-right font-mono-num text-zinc-400">{b.shares}</td>
                        <td className="py-2.5 text-right font-mono-num">{formatUSD(b.invested)}</td>
                        <td className="py-2.5 text-right font-mono-num">{formatUSD(b.current_value)}</td>
                        <td className="py-2.5 text-right">
                          <span className={`font-mono-num text-xs px-2 py-0.5 rounded ${b.gain_pct >= 0 ? "text-emerald-400 bg-emerald-500/10" : "text-rose-400 bg-rose-500/10"}`}>
                            {formatPct(b.gain_pct)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>

            {result.trades && result.trades.length > 0 && (
              <Card className="p-6 bg-card border-white/5" data-testid="wallet-trades">
                <h3 className="font-display font-bold text-lg mb-4">Derniers ordres ({result.trades.length})</h3>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="text-xs uppercase tracking-[0.15em] text-zinc-500">
                      <tr>
                        <th className="text-left py-2">Date</th>
                        <th className="text-left py-2">Actif</th>
                        <th className="text-left py-2">Action</th>
                        <th className="text-right py-2">Prix</th>
                        <th className="text-right py-2">Montant</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.trades.slice(-30).reverse().map((t, i) => (
                        <tr key={i} className="border-t border-white/5">
                          <td className="py-2 text-zinc-400 font-mono-num text-xs">{t.date}</td>
                          <td className="py-2 font-mono-num text-emerald-400 text-xs">{t.symbol}</td>
                          <td>
                            <span className={`text-xs font-mono-num px-2 py-0.5 rounded ${t.action === "BUY" ? "text-emerald-400 bg-emerald-500/10" : "text-rose-400 bg-rose-500/10"}`}>
                              {t.action}
                            </span>
                          </td>
                          <td className="text-right font-mono-num text-xs">{formatUSD(t.price)}</td>
                          <td className="text-right font-mono-num text-xs">{formatUSD(t.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
          )
        ) : (
          <Card className="p-16 bg-card border-white/5 text-center" data-testid="wallet-empty">
            <Wallet className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
            <div className="text-zinc-400">Composez votre wallet, réglez le DCA et le take-profit, puis simulez.</div>
            <div className="text-xs text-zinc-600 mt-2">Astuce : essayez « Comparer classique vs dynamique » pour voir l'écart de performance et de drawdown.</div>
          </Card>
        )}
      </div>
    </div>
  );
}
